import { stripMaskedPhoneSuffix } from "./personName";

/** Standard incoming: `ABC12XYZQR Confirmed.You have received...` */
const RE_CODE_LEADING = /^([A-Z0-9]{6,12})\b/i;

/** e.g. `Congratulations! UD50PBPNPP confirmed.You have received...` */
const RE_CODE_BEFORE_CONFIRMED = /\b([A-Z0-9]{8,12})\s+confirmed\.?\s*/i;

/** First amount in the “received” line (ignores later balance lines). */
const RE_AMOUNT_RECEIVED = /(?:Ksh|KES)\s?([\d,]+(?:\.\d{1,2})?)/i;

/**
 * Payer name after `from`, stopped before:
 * full MSISDN, masked `0723***113`, a plain long digit run, `on` / `at`, `.`, or end.
 */
const RE_SENDER_FROM =
  /from\s+(.+?)(?:\s+(?:\+?254|0)\d{9}|\s+0\d{2,4}\*{2,}\d*|\s+\d{9,12}|\s+on|\s+at|\.|$)/i;

const RE_PHONE_MSISDN = /(?:\+?254|0)\d{9}/;

const RE_DATETIME_ON_AT =
  /on\s+([0-9]{1,2}[\/.-][0-9]{1,2}[\/.-][0-9]{2,4})\s+at\s+([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM)?)/i;
const RE_DATE_ON = /on\s+([0-9]{1,2}[\/.-][0-9]{1,2}[\/.-][0-9]{2,4})/i;
const RE_TIME_AT = /at\s+([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM)?)/i;

export const normalizePhone = (value = "") => {
  const trimmed = String(value || "").replace(/[\s()-]/g, "");
  if (!trimmed) {
    return "";
  }
  const digits = trimmed.startsWith("+") ? trimmed.slice(1) : trimmed;
  if (/^0\d{9}$/.test(digits)) {
    return `254${digits.slice(1)}`;
  }
  if (/^254\d{9}$/.test(digits)) {
    return digits;
  }
  if (/^\d{9,15}$/.test(digits)) {
    return digits;
  }
  return "";
};

function normalizeMpesaText(message) {
  return String(message || "")
    .replace(/\s+/g, " ")
    .trim();
}

function extractConfirmationCode(normalized) {
  const leading = normalized.match(RE_CODE_LEADING);
  if (leading) {
    return leading[1].toUpperCase();
  }
  const beforeConfirmed = normalized.match(RE_CODE_BEFORE_CONFIRMED);
  if (beforeConfirmed) {
    return beforeConfirmed[1].toUpperCase();
  }
  return "";
}

function extractReceivedAmount(normalized) {
  const m = normalized.match(RE_AMOUNT_RECEIVED);
  if (!m) {
    return null;
  }
  const n = parseFloat(m[1].replace(/,/g, ""));
  return Number.isNaN(n) ? null : n;
}

function extractSender(normalized) {
  const m = normalized.match(RE_SENDER_FROM);
  if (!m) {
    return "";
  }
  return stripMaskedPhoneSuffix(m[1].trim());
}

function extractPhone(normalized) {
  const m = normalized.match(RE_PHONE_MSISDN);
  return m ? normalizePhone(m[0]) : "";
}

function extractTimestamp(normalized) {
  const dateTime = normalized.match(RE_DATETIME_ON_AT);
  if (dateTime) {
    return `${dateTime[1]} ${dateTime[2]}`;
  }
  const dateOnly = normalized.match(RE_DATE_ON);
  const timeOnly = normalized.match(RE_TIME_AT);
  if (dateOnly && timeOnly) {
    return `${dateOnly[1]} ${timeOnly[1]}`;
  }
  if (dateOnly) {
    return dateOnly[1];
  }
  if (timeOnly) {
    return timeOnly[1];
  }
  return "";
}

/**
 * @param {string} message
 * @param {{ now?: () => number }} [options] - `now` for synthetic `TS-…` when no reference is found (tests).
 */
export const parseMpesaMessage = (message = "", options = {}) => {
  const { now = () => Date.now() } = options;
  const normalized = normalizeMpesaText(message);

  const amount = extractReceivedAmount(normalized);
  const sender = extractSender(normalized);
  const phone = extractPhone(normalized);
  const timestamp = extractTimestamp(normalized);

  let code = extractConfirmationCode(normalized);
  const hasReadableAmount = amount != null && !Number.isNaN(amount);
  const hasSender = Boolean(sender);

  if (!code && hasReadableAmount && hasSender) {
    code = `TS-${now()}`;
  }

  return {
    code,
    amount,
    sender,
    phone,
    timestamp,
    raw: normalized,
    isValid: Boolean(code && hasReadableAmount && hasSender)
  };
};
